import { IOSettingsValue } from "./types";
import { Typography, Stack, Divider, Box } from "@mui/material";
import { IOPortItem } from "./types";
import { theme } from "@mb-pro-ui/components";
import ZoneChip, { IOZoneValueView } from "../ZoneView";
import { useIntl } from "react-intl";
import { behaviors, triggers } from "./SmartalarmBoard";
import { isEqual } from "lodash-es";

const ZonesOverview = ({ value, saved }: { value: IOSettingsValue; saved: IOSettingsValue | null }) => {
  const { formatMessage } = useIntl();

  const inputs = value.ports?.filter((port) => port.value?.type?.startsWith("IN")) || [];
  const outputs = value.ports?.filter((port) => port.value?.type === "OUT") || [];
  const disabled = value.ports?.filter((port) => port.value?.type?.startsWith("NULL")) || [];

  if (inputs.length + outputs.length + disabled.length === 0) {
    return null;
  }

  const zonesReducer = (acc: IOZoneValueView[], curr: IOPortItem) => {
    curr.value.zoneValues.forEach((zone, index) => {
      acc.push({ ...zone, port: curr.nr, deleted: !!curr.value.deleted || index >= curr.value.maxZones });
    });
    return acc;
  };

  const inputZones = inputs.reduce(zonesReducer, []);
  const inputZonesByBehavior = new Map<string, IOZoneValueView[]>();

  const outputZones = outputs.reduce(zonesReducer, []);
  const outputZonesByTrigger = new Map<string, IOZoneValueView[]>();

  const savedZones = saved ? saved.ports.reduce(zonesReducer, []) : [];
  const savedZonesByUuid = Object.fromEntries(savedZones.map((z) => [z.uuid, z]));

  inputZones
    .sort((a, b) => {
      const aBehavior = behaviors.find(({ value }) => parseInt(value, 2) === a.behavior);
      const bBehavior = behaviors.find(({ value }) => parseInt(value, 2) === b.behavior);

      if (!aBehavior || !bBehavior) {
        return 0;
      }

      return aBehavior.order - bBehavior.order;
    })
    .forEach((zone) => {
      const behavior = behaviors.find((behavior) => behavior.value === zone?.behavior.toString(2));
      const key = behavior ? behavior.value : "";
      const zoneValues = inputZonesByBehavior.get(key);
      if (zoneValues) {
        zoneValues.push(zone);
      } else {
        inputZonesByBehavior.set(key, [zone]);
      }
    });

  outputZones
    .sort((a, b) => {
      if (a.trigger && b.trigger) {
        return a.trigger.localeCompare(b.trigger);
      } else if (a.trigger) {
        return -1;
      }
      return 1;
    })
    .forEach((zone) => {
      const trigger = triggers.find((trigger) => trigger.value === zone?.trigger);
      const key = trigger ? trigger.value : "";
      const triggerValues = outputZonesByTrigger.get(key);
      if (triggerValues) {
        triggerValues.push(zone);
      } else {
        outputZonesByTrigger.set(key, [zone]);
      }
    });

  return (
    <Box
      sx={{
        padding: 1,
      }}
    >
      {inputs.length > 0 && (
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ m: 1 }}>
            {formatMessage({ defaultMessage: "Inputs" })}
          </Typography>
          {Array.from(inputZonesByBehavior.keys()).map((key) => {
            const zones = inputZonesByBehavior.get(key);
            const behavior = behaviors.find((behavior) => behavior.value === key);

            if (!behavior?.label || !zones || !(zones.filter((z) => z.behavior && z.zone).length > 0)) {
              return null;
            }

            return (
              <Box key={key} sx={{ mb: 2, ml: 2 }}>
                <Typography variant={"body2"} sx={{ color: behavior?.color }}>
                  {behavior?.label}
                </Typography>
                <Divider sx={{ borderColor: behavior?.color }} />
                <Stack direction="row" sx={{ flexWrap: "wrap" }}>
                  {zones
                    ?.filter((z) => z.behavior && z.zone)
                    .map((zone) => {
                      return (
                        <ZoneChip
                          key={zone.uuid}
                          zoneColor={behavior?.color}
                          icon={behavior?.icon}
                          zone={zone}
                          edited={!isEqual(zone, savedZonesByUuid[zone.uuid])}
                        />
                      );
                    })}
                </Stack>
              </Box>
            );
          })}
        </Box>
      )}
      {outputs.length > 0 && (
        <>
          {inputs.length > 0 && <Divider />}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ m: 1 }}>
              {formatMessage({ defaultMessage: "Outputs" })}
            </Typography>
            {Array.from(outputZonesByTrigger.keys()).map((key) => {
              const zones = outputZonesByTrigger.get(key);
              const trigger = triggers.find((trigger) => trigger.value === key);
              return (
                <Box key={key} sx={{ mb: 2, ml: 2 }}>
                  <Typography variant={"body2"} sx={{ color: trigger?.color }}>
                    {trigger?.label}
                  </Typography>
                  <Divider sx={{ borderColor: trigger?.color }} />
                  <Stack direction="row" sx={{ flexWrap: "wrap" }}>
                    {zones?.map((zone) => {
                      return <ZoneChip key={zone.uuid} zoneColor={trigger?.color} icon={trigger?.icon} zone={zone} />;
                    })}
                  </Stack>
                </Box>
              );
            })}
          </Box>
        </>
      )}
      {disabled.length > 0 && (
        <>
          {(inputs.length > 0 || outputZones.length > 0) && <Divider />}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ m: 1 }}>
              {formatMessage({ defaultMessage: "Disabled", description: "IOPortType.NULL" })}
            </Typography>
            <Box sx={{ px: 1, paddingBottom: 1 }}>
              {disabled.map((port, index) => (
                <Box
                  key={index}
                  component="span"
                  sx={{
                    backgroundColor: theme.palette.common.black,
                    color: theme.palette.getContrastText(theme.palette.common.black),
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    boxSizing: "border-box",
                    minWidth: "20px",
                    lineHeight: 1,
                    borderRadius: "10px",
                    padding: "0 4px",
                    height: "20px",
                    border: `2px solid ${theme.palette.common.white}`,
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >{`p${port.nr}`}</Box>
              ))}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default ZonesOverview;
